import axios, { AxiosResponse } from "axios";
import { ArrayFromEnvelope, configService, ObjectFromEnvelope } from "./config.service";
import {
  Employment,
  CreateEmploymentWithWorkHourPoolsPostback,
  SplitEmployment
} from "../models/employments";
import { convertDateOnlyToUtc } from "../utils/dateUtils";
import {
  eCalculationType,
  SupervisionTimeCalculation,
  SupervisionTimeCalculationGroup
} from "../models/supervisionTimeCalculations";
import { EmployeeTask } from "../models/task";
import { eEventBus, EventBus } from "@/eventbus";
import store from "@/store";
import { User } from "@/modules/security/models/user-model";
import { addMonths } from "date-fns";

class EmploymentsService {
  async fetchAllEmployementsByEmployeeId(employeeId: number): Promise<Employment[]> {
    const user: User = store.getters["security/getUser"]();
    if (user.isEmployee()) {
      return new Promise((resolve, reject) => {
        const utcFra = convertDateOnlyToUtc(user.employmentsFrom());
        const utcTilOgMed = convertDateOnlyToUtc(user.employmentsTo(addMonths(new Date(), 1)));

        axios
          .get<Employment[]>(configService.personaleOrAdminUrl("employments.json"), {
            params: {
              start_date: utcFra,
              end_date: utcTilOgMed,
              employee_id: employeeId
            }
          })
          .then((response: AxiosResponse) => {
            resolve(Employment.fromJSONArray(ArrayFromEnvelope<Employment>(response.data)));
          })
          .catch((error) => {
            reject(error);
          });
      });
    } else {
      return new Promise((resolve, reject) => {
        axios
          .get<Employment[]>(configService.APIInstitutionUrl("employments.json"), {
            params: {
              employee_id: employeeId
            }
          })
          .then((response: AxiosResponse) => {
            resolve(Employment.fromJSONArray(ArrayFromEnvelope<Employment>(response.data)));
          })
          .catch((error) => {
            reject(error);
          });
      });
    }
  }

  async createEmploymentTask(employment: EmployeeTask): Promise<EmployeeTask> {
    return new Promise((resolve, reject) => {
      axios
        .post<EmployeeTask>(configService.APIInstitutionUrl("work_assignments.json"), employment)
        .then((response: AxiosResponse) => {
          resolve(EmployeeTask.fromJSON(ObjectFromEnvelope<EmployeeTask>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async updateEmploymentTask(task: EmployeeTask): Promise<EmployeeTask> {
    return new Promise((resolve, reject) => {
      axios
        .put<EmployeeTask>(
          configService.APIInstitutionUrl("work_assignments/" + task.id + ".json"),
          task.convertToPutObject()
        )
        .then((response: AxiosResponse) => {
          resolve(EmployeeTask.fromJSON(ObjectFromEnvelope<EmployeeTask>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async deleteEmploymentTask(task: EmployeeTask): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete(configService.APIInstitutionUrl("work_assignments/" + task.id + ".json"))
        .then((response: AxiosResponse) => {
          resolve(response);
        })
        .catch((error) => reject(error));
    });
  }

  async splitEmployment(obj: SplitEmployment): Promise<void> {
    return new Promise((resolve, reject) => {
      axios
        .post(configService.APIInstitutionUrl("employments/change_employment_rate.json"), obj)
        .then(() => {
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  async fetchTasks(employmentId: number): Promise<EmployeeTask[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<EmployeeTask[]>(configService.personaleOrAdminUrl("work_assignments.json"), {
          params: {
            employment_id: employmentId
          }
        })
        .then((response: AxiosResponse) => {
          resolve(EmployeeTask.fromJSONArray(ArrayFromEnvelope<EmployeeTask>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async fetchEmployments(fra: Date, tilOgMed: Date): Promise<Employment[]> {
    return new Promise((resolve, reject) => {
      const utcFra = convertDateOnlyToUtc(fra);
      const utcTilOgMed = convertDateOnlyToUtc(tilOgMed);

      axios
        .get<Employment[]>(configService.personaleOrAdminUrl("employments.json"), {
          params: {
            institution_id: configService.institutionId(),
            start_date: utcFra,
            end_date: utcTilOgMed
          }
        })
        .then((response: AxiosResponse) => {
          resolve(Employment.fromJSONArray(ArrayFromEnvelope<Employment>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async fetchSupervisionTimeCalculationsByDate(
    date: Date,
    employmentId: number
  ): Promise<SupervisionTimeCalculationGroup[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<SupervisionTimeCalculationGroup[]>(
          configService.personaleOrAdminUrl(
            "supervision_time_calculations.json?employment_id=" +
              employmentId +
              "&end_date=" +
              convertDateOnlyToUtc(date)
          )
        )
        .then((response: AxiosResponse) => {
          const list: SupervisionTimeCalculationGroup[] = this.calcalationTypes(response);
          resolve(list);
        })
        .catch((error) => {
          EventBus.emit(eEventBus.BreakingDataError, {
            text: "",
            error: error
          });
          reject(error);
        });
    });
  }
  async fetchSupervisionTimeCalculations(
    employmentId: number
  ): Promise<SupervisionTimeCalculationGroup[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<SupervisionTimeCalculationGroup[]>(
          configService.personaleOrAdminUrl(
            "supervision_time_calculations.json?employment_id=" + employmentId
          )
        )
        .then((response: AxiosResponse) => {
          const list: SupervisionTimeCalculationGroup[] = this.calcalationTypes(response);
          resolve(list);
        })
        .catch((error) => {
          EventBus.emit(eEventBus.BreakingDataError, {
            text: "",
            error: error
          });
          reject(error);
        });
    });
  }
  private calcalationTypes(response: AxiosResponse<any>) {
    const list: SupervisionTimeCalculationGroup[] = [];
    const planned: SupervisionTimeCalculation = SupervisionTimeCalculation.fromJSON(
      response.data.data.planned
    );
    const used: SupervisionTimeCalculation = SupervisionTimeCalculation.fromJSON(
      response.data.data.used
    );
    const remaining: SupervisionTimeCalculation = SupervisionTimeCalculation.fromJSON(
      response.data.data.remaining
    );
    const status: SupervisionTimeCalculation = SupervisionTimeCalculation.fromJSON(
      response.data.data.status
    );

    list.push(new SupervisionTimeCalculationGroup(planned, eCalculationType.Planned));
    list.push(new SupervisionTimeCalculationGroup(used, eCalculationType.Used));
    list.push(new SupervisionTimeCalculationGroup(remaining, eCalculationType.Remaining));
    list.push(new SupervisionTimeCalculationGroup(status, eCalculationType.Status));
    return list;
  }

  async createEmployment(employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      axios
        .post<Employment>(
          configService.APIInstitutionUrl("employments.json"),
          employment.convertToPostObject()
        )
        .then((response: AxiosResponse) => {
          resolve(Employment.fromJSON(ObjectFromEnvelope<Employment>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }

  async createEmploymentWithWorkHourPools(
    employment: Employment,
    copy_from_employment_id: number
  ): Promise<CreateEmploymentWithWorkHourPoolsPostback> {
    return new Promise((resolve, reject) => {
      axios
        .post<Employment>(
          configService.APIInstitutionUrl("employments/create_with_work_hour_pools.json"),
          {
            employment: employment.convertToPostObject(),
            copy_from_employment_id: copy_from_employment_id
          }
        )
        .then((response: AxiosResponse) => {
          resolve(
            CreateEmploymentWithWorkHourPoolsPostback.fromJSON(
              ObjectFromEnvelope<CreateEmploymentWithWorkHourPoolsPostback>(response.data)
            )
          );
        })
        .catch((error) => reject(error));
    });
  }

  async updateEmployment(employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      axios
        .put<Employment>(
          configService.APIInstitutionUrl("employments/" + employment.id + ".json"),
          employment.convertToPutObject()
        )
        .then((response: AxiosResponse) => {
          resolve(Employment.fromJSON(ObjectFromEnvelope<Employment>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }

  async updateEmploymentSoCalculatesAs(employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      axios
        .put<Employment>(
          configService.APIInstitutionUrl(
            "employments/" + employment.id + "/update_so_calculates_as.json"
          ),
          { so_calculates_as: employment.so_calculates_as }
        )
        .then(() => {
          resolve(employment);
        })
        .catch((error) => reject(error));
    });
  }

  async deleteEmployment(employmentId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete<Employment>(
          configService.APIInstitutionUrl("employments/" + employmentId + ".json")
        )
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  }
}

export const employmentsService: EmploymentsService = new EmploymentsService();
